<script>
  import { onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faPencil, faTrash, faPlus, faXmark, faTrashCan } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import Footer from '$lib/Footer.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { checkPermission } from '$utils/permissionUtils.js';
  import { user } from '$stores/sessionStore.js';
  import RoleForm from './roles/RoleForm.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  
  const API_BASE_URL = '/api/proxy';

  let roles = [];
  let resultCount = 0;
  let currentPage = 1;
  let pageSize = 10;
  let totalPages = 1;
  let sortField = 'name';
  let sortOrder = 'asc';

  // Modal state
  let showRoleModal = false;
  let showDeleteModal = false;
  let selectedRole = null;
  let editingRole = null;

  // Permission types and resources for the form
  const resourceTypes = ['route', 'menu', 'field', 'action'];
  const accessTypes = ['read', 'write', 'create', 'delete', 'none'];

  onMount(async () => {
    fetchRoles();
  });

  async function fetchRoles() {
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        pageSize,
        sortField,
        sortOrder
      });

      const res = await fetch(
        `${API_BASE_URL}/roles?${queryParams}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch roles');
      }

      const response = await res.json();
      roles = Array.isArray(response.data) ? response.data : [];
      resultCount = response.totalCount || 0;
      currentPage = response.page || 1;
      totalPages = response.totalPages || 1;
    } catch (err) {
      console.error('Error fetching roles:', err);
      roles = [];
    }
  }

  function handleRefresh() {
    fetchRoles();
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchRoles();
  }

  function handleEditClick(role) {
    editingRole = role;
    showRoleModal = true;
  }

  function handleNewClick() {
    editingRole = null;
    showRoleModal = true;
  }

  function handleDeleteClick(role) {
    selectedRole = role;
    showDeleteModal = true;
  }

  async function handleRoleSubmit(event) {
    const roleData = event.detail;
    try {
      let url, method;

      if (editingRole) {
        // Update existing role
        url = `${API_BASE_URL}/roles/${editingRole._id}`;
        method = 'PUT';
      } else {
        // Create new role
        url = `${API_BASE_URL}/roles`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...roleData,
          createdBy: $user?._id,
          updatedBy: $user?._id
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${editingRole ? 'update' : 'create'} role`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || `Failed to ${editingRole ? 'update' : 'create'} role`);
      }

      showRoleModal = false;
      await fetchRoles();
    } catch (error) {
      console.error(`Error ${editingRole ? 'updating' : 'creating'} role:`, error);
      alert(error.message || `Failed to ${editingRole ? 'update' : 'create'} role`);
    }
  }

  async function handleDeleteConfirm() {
    if (!selectedRole) return;

    try {
      const response = await fetch(`${API_BASE_URL}/roles/${selectedRole._id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete role');
      }

      showDeleteModal = false;
      await fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
    }
  }

  function handlePageChange(page) {
    currentPage = page;
    fetchRoles();
  }

  // Format permission display
  function formatPermission(permission) {
    return `${permission.resource}:${permission.identifier} (${permission.access})`;
  }
</script>

<StudyHeader />

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Roles" itemCountTitle="Roles" itemCount={resultCount}>
  <div class="text-black dark:text-white flex items-center">
    <button 
      class="pagination mx-1 p-0 flex items-center" 
      on:click={handleNewClick}
      title="Create Role"
    >
      <FontAwesomeIcon icon={faPlus} />
    </button>
    <button class="pagination mx-1 p-0" on:click={handleRefresh}>
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('name')}>Name</th>
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('displayName')}>Display Name</th>
        <th class="px-4 py-2 text-left bold">Description</th>
        <th class="px-4 py-2 text-left bold">Permissions</th>
        <th class="px-4 py-2 text-left bold">Status</th>
        <th class="px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>

    <tbody>
      {#each roles as role, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2">{role.name}</td>
          <td class="px-4 py-2">{role.displayName}</td>
          <td class="px-4 py-2">{role.description}</td>
          <td class="px-4 py-2">
            <div class="max-h-20 overflow-y-auto">
              {#each role.permissions as permission}
                <div class="text-xs">{formatPermission(permission)}</div>
              {/each}
            </div>
          </td>
          <td class="px-4 py-2">
            <span
              class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              class:text-green-700={role.isActive}
              class:ring-green-700={role.isActive}
              class:text-slate-600={!role.isActive}
              class:ring-slate-600={!role.isActive}
            >
              {role.isActive ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td class="px-4 py-2">
            <div class="flex space-x-2">
              <button 
                class="text-sky-800 dark:text-white/70"
                on:click={() => handleEditClick(role)}
                title="Edit Role"
                disabled={role.isSystem}
                class:opacity-50={role.isSystem}
                class:cursor-not-allowed={role.isSystem}
              >
                <FontAwesomeIcon icon={faPencil} />
              </button>
              <button 
                class="text-sky-800 dark:text-white/70"
                on:click={() => handleDeleteClick(role)}
                title="Delete Role"
                disabled={role.isSystem}
                class:opacity-50={role.isSystem}
                class:cursor-not-allowed={role.isSystem}
              >
                <FontAwesomeIcon icon={faTrashCan} />
              </button>
            </div>
          </td>
        </tr>
      {/each}
      {#if roles.length === 0}
        <tr>
          <td colspan="6" class="text-center text-gray-500 dark:text-slate-400 mt-10">No roles found</td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<RoleForm
  showForm={showRoleModal}
  editRole={editingRole}
  on:submit={handleRoleSubmit}
  on:close={() => (showRoleModal = false)}
/>

<ConfirmationModal
  show={showDeleteModal}
  message={`Are you sure you want to delete the role "${selectedRole?.displayName}"? This action cannot be undone.`}
  on:confirm={handleDeleteConfirm}
  on:close={() => (showDeleteModal = false)}
/>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer> 